import { ChevronDown } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { endMetadataMerge } from "../../lib/metadata-history/dispatch";
import type { MetadataTarget } from "../../lib/metadata-history/metadata-targets";
import { useMetadataEditingStore } from "../../stores/metadata-editing-store";
import { useCanvasDayAnchorsEnabled, useCanvasMetadataTargets } from "../../lib/schedule-art/canvas-state";
import { metadataLabels } from "../editor-panel/developer-section/metadata-form-schema";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { metadataTargetLabel } from "./metadata-target-label";

function sectionKey(target: MetadataTarget) {
  return target.day ?? "globals";
}

function groupBySection(targets: MetadataTarget[]) {
  const keys = [...new Set(targets.map(sectionKey))];
  return keys.map((key) => ({ key, targets: targets.filter((target) => sectionKey(target) === key) }));
}

export function MetadataTargetSelect() {
  const { t } = useTranslation();
  const canvasTargets = useCanvasMetadataTargets();
  const dayAnchorsEnabled = useCanvasDayAnchorsEnabled();
  const selected = useMetadataEditingStore((state) => state.selected);
  const renderedPaths = useMetadataEditingStore((state) => state.renderedPaths);
  const select = useMetadataEditingStore((state) => state.select);
  const labels = useMemo(() => metadataLabels(t), [t]);
  const groups = useMemo(() => {
    const targets = canvasTargets.filter(target => {
      if (!dayAnchorsEnabled && target.bind === "origin") return false;
      return true;
    });
    return groupBySection(targets);
  }, [canvasTargets, dayAnchorsEnabled]);
  const [open, setOpen] = useState(false);
  const rendered = new Set(renderedPaths);
  const notRendered = t("metadata.canvas.notRendered");
  const selectedTarget = groups.flatMap((group) => group.targets).find((target) => target.path === selected);
  const triggerLabel = selectedTarget ? metadataTargetLabel(selectedTarget, labels) : t("metadata.canvas.none");
  const triggerColor = selectedTarget ? "" : "text-neutral-400";

  const choose = (path: string) => {
    endMetadataMerge();
    select(path);
    setOpen(false);
  };

  const optionColor = (target: MetadataTarget) => {
    const isSelected = target.path === selected;
    if (isSelected) return "bg-sky-100 text-sky-800";
    const isVisible = target.bind === "origin" || rendered.has(target.path);
    return isVisible ? "hover:bg-neutral-100" : "text-neutral-400 hover:bg-neutral-100";
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        className={`flex h-8 min-w-0 flex-[1_1_12rem] items-center justify-between gap-2 rounded-md border border-neutral-200 bg-white px-2 outline-none focus:border-sky-400 ${triggerColor}`}
      >
        <span className="truncate">{triggerLabel}</span>
        <ChevronDown className="size-3.5 shrink-0 text-neutral-400" />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="max-h-[min(20rem,var(--radix-popover-content-available-height))] w-(--radix-popover-trigger-width) min-w-56 overflow-y-auto p-0 pb-1 text-xs"
      >
        {groups.map((group) => (
          <div key={group.key}>
            <div className="sticky top-0 z-10 border-b border-neutral-100 bg-white px-3 py-1.5 font-semibold text-neutral-500">
              {labels[group.key]}
            </div>
            {group.targets.map((target) => (
              <button
                key={target.path}
                type="button"
                ref={target.path === selected ? (node) => node?.scrollIntoView({ block: "nearest" }) : undefined}
                title={target.bind === "origin" || rendered.has(target.path) ? undefined : notRendered}
                className={`mx-1 block w-[calc(100%-0.5rem)] truncate rounded px-2 py-1.5 text-left ${optionColor(target)}`}
                onClick={() => choose(target.path)}
              >
                {metadataTargetLabel(target, labels, false)}
              </button>
            ))}
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
}
